import {useMemo} from 'react';
import {useColorModeValue} from '@chakra-ui/react';

export const HIDDEN_SCROLLBAR_SX = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

// A dedicated, always-visible scrollbar for the planner's main scroll regions
// (left list, the right-hand detail column, the patient workspace, and the
// encounter/session detail panels). The doctors using this product are not
// power users and often work on Macs where overlay scrollbars stay hidden
// until you scroll — so a hidden scrollbar leaves them with no obvious handle
// to grab. This token forces a real, rounded scrollbar with a generous 12px
// hit area (comfortable even on twitchy, high-DPI mice), a faint pill track so
// the scroll lane is discoverable at rest, and an Opal-blue thumb on
// hover/drag so it reads as an interactive control rather than chrome.
//
// Returned from a hook so the colors track Chakra's light/dark mode. Use the
// `sx` prop: `sx={useScrollbarSx()}`.
export function useScrollbarSx() {
  // Resting thumb: neutral and quiet so it never fights the content.
  const thumbColor = useColorModeValue(
    'rgba(26, 58, 74, 0.28)',
    'rgba(226, 232, 240, 0.26)',
  );
  // Hover/drag stay grey — just a touch darker for tactile feedback, never
  // colored, so the scrollbar always reads as neutral chrome.
  const thumbHoverColor = useColorModeValue(
    'rgba(26, 58, 74, 0.45)',
    'rgba(226, 232, 240, 0.42)',
  );
  const thumbActiveColor = useColorModeValue(
    'rgba(26, 58, 74, 0.6)',
    'rgba(226, 232, 240, 0.55)',
  );
  // The lane itself — barely-there at rest, just enough to telegraph "scroll".
  const trackColor = useColorModeValue(
    'rgba(26, 58, 74, 0.06)',
    'rgba(226, 232, 240, 0.08)',
  );

  return useMemo(
    () => ({
      // Firefox only. The standard properties MUST stay scoped to Gecko: per
      // the CSSWG resolution — shipped in Chrome 121+ and Safari 18.2+ — a
      // non-initial scrollbar-width/scrollbar-color makes the engine ignore
      // every ::-webkit-scrollbar rule on the element. Unscoped, they
      // silently replaced this custom bar with macOS's auto-hiding overlay
      // scrollbar, which vanished at rest and painted over the content when
      // it did show. (`@supports selector(::-webkit-scrollbar)` can't be the
      // guard — Firefox 151+ parses that selector too — so gate on
      // -moz-appearance, which only Gecko supports.)
      '@supports (-moz-appearance: none)': {
        scrollbarWidth: 'thin',
        scrollbarColor: `${thumbColor} ${trackColor}`,
        '&:hover': {
          scrollbarColor: `${thumbHoverColor} ${trackColor}`,
        },
      },
      // WebKit / Chromium / Safari. Styling ::-webkit-scrollbar also opts the
      // element out of overlay scrollbars, so the bar stays visible at rest
      // and occupies its own layout lane instead of floating over content.
      '&::-webkit-scrollbar': {
        width: '12px',
        height: '12px',
      },
      '&::-webkit-scrollbar-track': {
        background: trackColor,
        borderRadius: '9999px',
        margin: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: thumbColor,
        borderRadius: '9999px',
        // Transparent border + padding-box clip floats a slim pill inside the
        // wider hit area, so the handle stays easy to grab but looks delicate.
        border: '3px solid transparent',
        backgroundClip: 'padding-box',
        // A tall minimum keeps the thumb catchable even in very long lists.
        minHeight: '48px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: thumbHoverColor,
        border: '2px solid transparent',
        backgroundClip: 'padding-box',
      },
      '&::-webkit-scrollbar-thumb:active': {
        background: thumbActiveColor,
        border: '2px solid transparent',
        backgroundClip: 'padding-box',
      },
    }),
    [thumbColor, thumbHoverColor, thumbActiveColor, trackColor],
  );
}

// Single source of truth for the planner workspace palette. The schedule
// list, the encounter composer, and the schedule modal all read from this
// hook so the surfaces cannot drift apart. Opalite is reserved for selection
// and primary actions; everything else stays neutral.
export default function usePlannerStyles() {
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const surfaceBg = useColorModeValue('white', 'whiteAlpha.50');
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtleText = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('opalite.500', 'opalite.500');
  const trackBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const metaPillBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const errorTextColor = useColorModeValue('red.600', 'red.300');
  const sidebarSurfaceBg = useColorModeValue('white', 'gray.900');
  const sidebarTextColor = useColorModeValue('gray.800', 'gray.100');
  const sidebarMutedTextColor = useColorModeValue('gray.600', 'gray.300');
  // One step lighter/dimmer than the muted metadata text — for secondary
  // captions like the recent-row "<duration> • Interpreter/Scribe" note.
  const captionTextColor = useColorModeValue('gray.500', 'gray.400');
  const sidebarBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const sidebarDividerColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const sidebarHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const sidebarCountBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const listRowFadeBg = sidebarSurfaceBg;
  const listRowHoverBg = sidebarHoverBg;
  const listRowSelectedBg = useColorModeValue(
    'opalite.50',
    'rgba(30, 159, 212, 0.16)',
  );
  const listRowSelectedAlternateBg = useColorModeValue(
    'opalite.100',
    'rgba(30, 159, 212, 0.24)',
  );
  const listRowSelectedBorderColor = useColorModeValue(
    'opalite.300',
    'opalite.400',
  );
  const listRowSelectedColor = sidebarTextColor;
  const cardShadow = useColorModeValue(
    '0 1px 2px rgba(23, 23, 23, 0.04), 0 12px 28px -14px rgba(23, 23, 23, 0.16)',
    '0 1px 2px rgba(0, 0, 0, 0.4)',
  );
  const segmentBorderColor = useColorModeValue('gray.300', 'whiteAlpha.400');
  const selectedSegmentShadow = useColorModeValue(
    '0 1px 2px rgba(23, 23, 23, 0.12)',
    '0 1px 2px rgba(0, 0, 0, 0.4)',
  );
  const focusShadow = useColorModeValue(
    '0 0 0 3px var(--chakra-colors-opalite-600)',
    '0 0 0 3px var(--chakra-colors-opalite-300)',
  );
  const focusOutlineColor = useColorModeValue('opalite.600', 'opalite.300');
  // Modality "type" pills (Interpreter / Scribe / Phone). Each tone reuses the
  // app-wide workflow vocabulary — teal `interp`, indigo `scribe`, blue
  // `opalite` — so a row's type reads the same here as on the Continue picker
  // and home cards. Text/fill pairs are chosen to clear WCAG AA (>=4.5:1) in
  // both color modes.
  const pillInterpreterBg = useColorModeValue(
    'interp.100',
    'rgba(94, 175, 150, 0.18)',
  );
  const pillInterpreterColor = useColorModeValue('interp.700', 'interp.300');
  const pillInterpreterBorder = useColorModeValue(
    'interp.300',
    'rgba(94, 175, 150, 0.4)',
  );
  const pillScribeBg = useColorModeValue(
    'scribe.100',
    'rgba(99, 102, 241, 0.2)',
  );
  const pillScribeColor = useColorModeValue('scribe.700', 'scribe.300');
  const pillScribeBorder = useColorModeValue(
    'scribe.300',
    'rgba(99, 102, 241, 0.42)',
  );
  const pillPhoneBg = useColorModeValue(
    'opalite.100',
    'rgba(30, 159, 212, 0.2)',
  );
  const pillPhoneColor = useColorModeValue('opalite.700', 'opalite.300');
  const pillPhoneBorder = useColorModeValue(
    'opalite.300',
    'rgba(30, 159, 212, 0.42)',
  );
  const pillNeutralBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const pillNeutralColor = useColorModeValue('gray.700', 'gray.200');
  const pillNeutralBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  return useMemo(
    () => ({
      // Shared surfaces
      pageBg,
      surfaceBg,
      panelBg,
      borderColor,
      hoverBg: panelBg,
      textColor,
      subtleText,
      accentColor,
      trackBg,
      errorTextColor,

      // Schedule rows
      rowBg: sidebarSurfaceBg,
      rowBorderColor: sidebarBorderColor,
      rowHoverBg: sidebarHoverBg,
      rowActiveBg: listRowSelectedBg,
      rowActionColor: sidebarTextColor,
      rowTimeColor: sidebarMutedTextColor,
      listRowFadeBg,
      listRowDividerColor: sidebarDividerColor,
      listRowHoverBg,
      listRowHoverFadeBg: listRowHoverBg,
      listRowSelectedBg,
      listRowSelectedAlternateBg,
      listRowSelectedBorderColor,
      listRowSelectedColor,
      listRowSelectedTimeColor: listRowSelectedColor,
      listRowSelectedFadeBg: listRowSelectedBg,
      metaTextColor: sidebarMutedTextColor,
      captionTextColor,
      pillTones: {
        interpreter: {
          bg: pillInterpreterBg,
          color: pillInterpreterColor,
          borderColor: pillInterpreterBorder,
        },
        scribe: {
          bg: pillScribeBg,
          color: pillScribeColor,
          borderColor: pillScribeBorder,
        },
        phone: {
          bg: pillPhoneBg,
          color: pillPhoneColor,
          borderColor: pillPhoneBorder,
        },
        neutral: {
          bg: pillNeutralBg,
          color: pillNeutralColor,
          borderColor: pillNeutralBorder,
        },
      },
      metaPillBg: trackBg,
      metaPillColor: subtleText,
      metaPillBorderColor,
      sectionLabelColor: sidebarTextColor,
      countBg: sidebarCountBg,
      countColor: sidebarTextColor,
      // Composer form
      cardShadow,
      selectedSegmentBg: surfaceBg,
      selectedSegmentBorderColor: segmentBorderColor,
      selectedSegmentColor: textColor,
      selectedSegmentShadow,
      focusShadow,
      focusOutlineStyles: {
        outline: '2px solid',
        outlineColor: focusOutlineColor,
        outlineOffset: '2px',
      },
      inputFocusStyles: {
        borderColor: focusOutlineColor,
        outline: '2px solid',
        outlineColor: focusOutlineColor,
        outlineOffset: '2px',
      },
      insetInputFocusStyles: {
        boxShadow: 'none',
        borderColor: focusOutlineColor,
        outline: '2px solid',
        outlineColor: focusOutlineColor,
        outlineOffset: '-2px',
      },
      modalBg: surfaceBg,
      typeaheadHoverBg: panelBg,
      typeaheadSelectedBg: listRowSelectedBg,
      typeaheadSelectedBorder: listRowSelectedBorderColor,
      commonBadgeBg: panelBg,
      commonBadgeColor: subtleText,
      controlFontSize: 'sm',
    }),
    [
      accentColor,
      borderColor,
      cardShadow,
      errorTextColor,
      focusOutlineColor,
      focusShadow,
      listRowFadeBg,
      listRowHoverBg,
      listRowSelectedAlternateBg,
      listRowSelectedBorderColor,
      listRowSelectedBg,
      listRowSelectedColor,
      metaPillBorderColor,
      pillInterpreterBg,
      pillInterpreterColor,
      pillInterpreterBorder,
      pillScribeBg,
      pillScribeColor,
      pillScribeBorder,
      pillPhoneBg,
      pillPhoneColor,
      pillPhoneBorder,
      pillNeutralBg,
      pillNeutralColor,
      pillNeutralBorder,
      pageBg,
      panelBg,
      sidebarBorderColor,
      sidebarCountBg,
      sidebarDividerColor,
      sidebarHoverBg,
      sidebarMutedTextColor,
      sidebarSurfaceBg,
      sidebarTextColor,
      segmentBorderColor,
      selectedSegmentShadow,
      subtleText,
      surfaceBg,
      textColor,
      trackBg,
    ],
  );
}
